import { useMemo } from 'react'
import { PackageNames } from '../../store/interface';
import pricing from "../../assets/data/pricing.json";

interface IPackages {
    key: PackageNames;
    discountAmount?: string;
    price: string;
    currencySymbol: string;
    defaultPrice?: string;
    countryTier?: 'A' | 'B' | 'C' | 'D';
    couponCode?: string;
}
const packages = Object.values(PackageNames);

const getOnlyDigits = (str: string) => {
    return str.replace(/[^0-9.,]+/g, '');
};

const calculatePercentBetweenTwoPrices = (oldPrice: number, newPrice: number) => {
    return parseFloat(((oldPrice - newPrice) / oldPrice).toFixed(2)) * 100;
};


const getJsonKeyFromTitle = (
    title: PackageNames
): "3months" | "12months" | "24months" => {
    switch (title) {
        case PackageNames["Quarterly Subscription"]:
            return "3months";
        case PackageNames["Yearly Subscription"]:
            return "12months";
        case PackageNames["Biennial Subscription"]:
            return "24months";
        default:
            return "3months";
    }
};
const getJsonStrikeThroughFromTitle = (
    title: PackageNames
): "12monthsStrikethrough" | "24monthsStrikethrough" | undefined => {
    switch (title) {
        case PackageNames["Quarterly Subscription"]:
            return undefined;
        case PackageNames["Yearly Subscription"]:
            return "12monthsStrikethrough";
        case PackageNames["Biennial Subscription"]:
            return "24monthsStrikethrough";
        default:
            return undefined;
    }
};

export const getMonthFromProductMonthType = (value: PackageNames) => {
    switch (value) {
        case PackageNames["Quarterly Subscription"]:
            return 3;
        case PackageNames["Yearly Subscription"]:
            return 12;
        case PackageNames["Biennial Subscription"]:
            return 24;
        default:
            return 1;
    }
}

const usePricingHook = (country: string) => {



    const packagePricing = useMemo(() => {
        const filteredPricing = pricing.find(e => e.countryCode === country);
        if (!filteredPricing) return;
        const buildPricing = (value: PackageNames): IPackages => {
            let priceString = filteredPricing[getJsonKeyFromTitle(value)].toString();
            const getMultiplier = +getMonthFromProductMonthType(value);

            if (!isNaN(getMultiplier)) {
                priceString = (+priceString / +getMultiplier).toString();
            }
            let symbolString = filteredPricing.currency;
            const itemStrikethrough = getJsonStrikeThroughFromTitle(value);
            let defaultPrice = '';

            if (itemStrikethrough) {
                const strikeThroughPrice = getOnlyDigits(filteredPricing[itemStrikethrough].toString());
                defaultPrice = (+strikeThroughPrice / +getMonthFromProductMonthType(value)).toString();
            } else {
                defaultPrice = priceString;
            }

            let discountAmount: string | undefined;
            let price = priceString;


            const calculatedDiscount = calculatePercentBetweenTwoPrices(+defaultPrice, +priceString);
            if (calculatedDiscount <= 0) {
                discountAmount = undefined;
                defaultPrice = '';
            } else {
                discountAmount = `${calculatedDiscount.toFixed(0)}%`;
            }

            return {
                key: value,
                price,
                currencySymbol: symbolString,
                discountAmount,
                defaultPrice,
                countryTier: filteredPricing.countryTier as 'A' | 'B' | 'C' | 'D',
            };
        };
        return packages.map(buildPricing);
    }, [country]);


    return { packagePricing }
}

export default usePricingHook